import styled from "@emotion/styled";
import { Add } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { Trash } from "@phosphor-icons/react";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import { useChartOfAccounts, useCreateJournalEntry } from "features/finances";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsXSquare } from "react-icons/bs";
import { NumericFormat } from "react-number-format";
import { EthiopianDateField } from "../EthiopianDateField";

const CloseIcon = styled(BsXSquare)`
  color: #ffffff;
  height: 22px;
  width: 22px;
  font-weight: bold;
  margin-left: auto;
  cursor: pointer;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #fafafa;

  th {
    color: #8e8e8e;
  }
`;

export const JournalEntryModal = ({ open, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [rows, setRows] = useState([
    {
      account: "",
      debit: 0,
      credit: 0,
      description: ""
    }
  ]);

  const { t } = useTranslation();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id
  });

  const createJournalEntryMutation = useCreateJournalEntry();

  const handleSave = () => {
    const data = rows.map(row => ({
      entry_date: selectedDate,
      entry_type: row.debit > 0 ? "debit" : "credit",
      amount: row.debit > 0 ? row.debit : row.credit,
      description: row.description,
      chart_of_account_id: row.account
    }));

    createJournalEntryMutation.mutate(
      {
        churchId: selectedChurch.id,
        journalEntryData: {
          entries: data
        }
      },
      {
        onSuccess: () => {
          setRows([
            {
              account: "",
              debit: 0,
              credit: 0,
              description: ""
            }
          ]);
          setSelectedDate(moment().format("YYYY-MM-DD"));
          onClose();
        }
      }
    );
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle sx={{ bgcolor: "primary.main" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" color="primary.contrastText">
            Journal Entry
          </Typography>
          <CloseIcon onClick={onClose} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} my={2}>
          <Stack alignItems="flex-start" width="25%">
            <EthiopianDateField
              size="small"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </Stack>
          <TableContainer>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Debit</TableCell>
                  <TableCell>Credit</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Select
                        value={row.account}
                        onChange={e => {
                          const newRows = [...rows];
                          newRows[index].account = e.target.value;
                          setRows(newRows);
                        }}
                        fullWidth
                        size="small"
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Account
                        </MenuItem>
                        {chartOfAccountsQuery.data?.result?.map(account => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.account_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        customInput={TextField}
                        id="debit"
                        thousandSeparator
                        allowNegative={false}
                        value={row.debit}
                        onValueChange={values => {
                          const newRows = [...rows];
                          newRows[index].debit = values.floatValue;
                          setRows(newRows);
                        }}
                        fullWidth
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        customInput={TextField}
                        id="credit"
                        thousandSeparator
                        allowNegative={false}
                        value={row.credit}
                        onValueChange={values => {
                          const newRows = [...rows];
                          newRows[index].credit = values.floatValue;
                          setRows(newRows);
                        }}
                        fullWidth
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.description}
                        onChange={e => {
                          const newRows = [...rows];
                          newRows[index].description = e.target.value;
                          setRows(newRows);
                        }}
                        fullWidth
                        size="small"
                        placeholder="Write description"
                      />
                    </TableCell>
                    <TableCell>
                      {rows.length > 1 && (
                        <IconButton
                          onClick={() => {
                            const newRows = [...rows];
                            newRows.splice(index, 1);
                            setRows(newRows);
                          }}
                        >
                          <Trash />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            flavor="outlined"
            startIcon={<Add />}
            onClick={() => {
              const newRow = {
                account: "",
                debit: 0,
                credit: 0,
                description: ""
              };
              setRows([...rows, newRow]);
            }}
            style={{
              padding: "8px 24px",
              color: "#000",
              borderColor: "#000",
              borderWidth: 1,
              alignSelf: "flex-start"
            }}
          >
            Add Field
          </Button>

          <MutationError
            error={createJournalEntryMutation.error}
            sx={{ mt: 2 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          flavor="text"
          onClick={onClose}
          disabled={createJournalEntryMutation.isLoading}
        >
          {t("form.common.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={createJournalEntryMutation.isLoading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
